/**
 * Algolia Analytics Integration
 * Tracks clicks and add-to-cart using the Algolia Insights API. Search events
 * are sent by AlgoliaShared.reportSearch (algolia-shared.js).
 */

class LampsAlgoliaAnalytics {
  constructor() {
    // Validate configuration
    if (!window.algoliaConfig || !window.algoliaConfig.enabled) {
      return;
    }

    if (!window.algoliaConfig.features.analytics) {
      return;
    }

    this.config = window.algoliaConfig;
    this.insightsReady = false;

    // Search Insights is loaded by its own deferred script tag
    // Wait for it to be available before initializing
    this.waitForInsights();
  }

  /**
   * Wait for Search Insights library to load (CDN async loading)
   */
  waitForInsights(attempts = 0) {
    const maxAttempts = window.AlgoliaShared?.MAX_RETRY_ATTEMPTS || 15;

    // Check for AlgoliaAnalytics.default (preferred) or aa function
    const insightsAvailable = (
      (typeof window.AlgoliaAnalytics !== 'undefined' && window.AlgoliaAnalytics.default) ||
      typeof aa === 'function'
    );

    if (insightsAvailable) {
      this.initializeInsights();
      this.setupTrackingListeners();
      this.log('Analytics initialized');
      return;
    }

    if (attempts >= maxAttempts) {
      console.warn('[Algolia Analytics] Search Insights library not loaded after ' + maxAttempts + ' attempts');
      return;
    }

    // Retry after delay
    const retryDelay = window.AlgoliaShared?.RETRY_DELAY_MS || 200;
    setTimeout(() => this.waitForInsights(attempts + 1), retryDelay);
  }

  /**
   * Initialize Algolia Insights (uses shared utility to prevent duplicate init)
   */
  initializeInsights() {
    // Use shared userToken (single source of truth)
    const userToken = window.AlgoliaShared?.getUserToken() || this.getFallbackUserToken();

    // Check if already initialized by another component (e.g., collection page)
    if (window.AlgoliaShared?.isInsightsInitialized()) {
      this.insightsReady = true;
      this.log('Insights already initialized by another component, reusing. User token:', userToken);
      return;
    }

    // Try to initialize via shared utility
    if (window.AlgoliaShared?.initInsights(this.config)) {
      this.insightsReady = true;
      this.log('Insights initialized via AlgoliaShared with user token:', userToken);
      return;
    }

    // Fallback: direct initialization if shared utility unavailable
    const insights = window.AlgoliaAnalytics?.default;
    if (insights) {
      insights.init({
        appId: this.config.appId,
        apiKey: this.config.apiKey,
        useCookie: true,
        userHasOptedOut: !!window.lampsPreviewBuild
      });
      insights.setUserToken(userToken);
      window.AlgoliaShared?.markInsightsInitialized();
    } else if (typeof aa === 'function') {
      aa('init', {
        appId: this.config.appId,
        apiKey: this.config.apiKey,
        userHasOptedOut: !!window.lampsPreviewBuild
      });
      aa('setUserToken', userToken);
      window.AlgoliaShared?.markInsightsInitialized();
    }

    this.insightsReady = true;
    this.log('Insights initialized with user token:', userToken);
  }

  /**
   * Fallback user token if AlgoliaShared not available
   */
  getFallbackUserToken() {
    // Priority 1: Customer ID from algoliaConfig (stable across email changes)
    if (window.algoliaConfig?.userToken) {
      return window.algoliaConfig.userToken;
    }
    // Priority 2: Anonymous localStorage token
    const storageKey = 'algolia_user_token';
    let token = localStorage.getItem(storageKey);
    if (!token) {
      token = 'user_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem(storageKey, token);
    }
    return token;
  }

  /**
   * Store conversion data for Shopify Web Pixel
   * The pixel runs in checkout sandbox — we store in localStorage only
   * (cookie fallback removed to prevent 431 "cookie too large" errors)
   * @param {string} queryID - The Algolia queryID from search
   * @param {string} objectID - The product objectID
   * @param {string} indexName - The Algolia index name
   */
  storeConversionData(queryID, objectID, indexName, extraData) {
    if (!queryID || !objectID) return;

    try {
      const storageKey = '_algolia_conversion';
      const existingData = this.getConversionData() || { items: [] };

      // Add new item (keep last N items to prevent overflow)
      const maxItems = window.AlgoliaShared?.MAX_CONVERSION_ITEMS || 20;
      existingData.items = existingData.items.filter(item => item.objectID !== objectID);
      const item = {
        queryID,
        objectID,
        indexName: indexName || this.config.indexName,
        timestamp: Date.now()
      };
      if (extraData) {
        if (extraData.price) item.price = extraData.price;
        if (extraData.quantity) item.quantity = extraData.quantity;
        if (extraData.discount) item.discount = extraData.discount;
        if (extraData.currency) item.currency = extraData.currency;
      }
      existingData.items.unshift(item);
      existingData.items = existingData.items.slice(0, maxItems);

      const dataStr = JSON.stringify(existingData);

      // Store in localStorage (Shopify Web Pixel can access via browser.localStorage.getItem)
      try {
        localStorage.setItem(storageKey, dataStr);
      } catch (_e) {
        // localStorage not available
      }

      // Expire any leftover _algolia_conversion cookie from previous versions
      document.cookie = '_algolia_conversion=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';

      this.log('Stored conversion data:', { queryID, objectID });
    } catch (e) {
      this.log('Failed to store conversion data:', e);
    }
  }

  /**
   * Get conversion data from localStorage
   */
  getConversionData() {
    try {
      const stored = localStorage.getItem('_algolia_conversion');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (_e) {
      // localStorage not available
    }
    return null;
  }

  /**
   * Set up tracking listeners
   */
  setupTrackingListeners() {
    // Search events come from AlgoliaShared.observeSearch / reportSearch,
    // which the search surfaces call directly.

    // Track product clicks
    this.setupClickTracking();

    // Track add to cart
    this.setupAddToCartTracking();

    // Conversion tracking is handled exclusively by the Shopify Web Pixel
    // (algolia-conversion-pixel.js) which fires purchasedObjectIDsAfterSearch
    // with full objectData. No fallback needed here — it caused duplicate events.

    this.log('Tracking listeners set up');
  }

  /**
   * Set up click tracking on product links
   */
  setupClickTracking() {
    document.addEventListener('click', (e) => {
      // Skip if a page-specific adapter is managing click tracking.
      // Check lastHits (set on first render) rather than _clickTrackingHandler
      // (set after async Insights init) to avoid a race window where both fire.
      const adapter = window.algoliaCollectionPage || window.algoliaSearchPage;
      if (adapter?.lastHits?.length) {
        return;
      }

      // Check if clicked element is a product link
      const productLink = e.target.closest('a[href*="/products/"]');

      if (!productLink) {
        return;
      }

      // Check if it's from Algolia results
      const productCard = productLink.closest('.product-card, .predictive-search__result-link');

      if (!productCard) {
        return;
      }

      // Get product handle from URL for position lookup
      const href = productLink.getAttribute('href');
      const productHandle = href.match(/\/products\/([^/?]+)/)?.[1];

      if (!productHandle) {
        return;
      }

      // Extend adapter to include theme adapter fallback
      const fullAdapter = adapter || window.algoliaAdapter;

      // Get queryID - try multiple sources
      // 1. adapter's lastQueryID property (set by collection page or search page)
      // 2. helper.lastResults.queryID (InstantSearch internal)
      const queryID = fullAdapter?.lastQueryID || fullAdapter?.search?.helper?.lastResults?.queryID;

      // Get objectID and position from lastHits
      const { objectID, position } = this.getProductFromHits(productHandle, fullAdapter);
      const indexName = fullAdapter?.search?.helper?.state?.index || this.config.indexName;

      if (!objectID) {
        this.log('Product not found in hits:', productHandle);
        return;
      }

      if (queryID) {
        // Search-attributed click
        this.sendInsightsEvent('clickedObjectIDsAfterSearch', {
          eventName: 'Product Clicked',
          index: indexName,
          objectIDs: [objectID],
          positions: [position],
          queryID: queryID
        });

        // Store for conversion tracking via Shopify Web Pixel
        this.storeConversionData(queryID, objectID, indexName);

        // Also store in cart attribution for line item properties (primary method)
        if (window.AlgoliaCartAttribution) {
          const helper = adapter?.search?.helper;
          const state = helper?.state;
          window.AlgoliaCartAttribution.store({
            queryID: queryID,
            objectID: objectID,
            handle: productHandle,
            indexName,
            position: position,
            query: state?.query || '',
            filters: this.getFiltersString(state)
          });
        }

        this.log('Click tracked:', objectID, 'position:', position, 'queryID:', queryID);
      } else {
        // Fallback: non-search click (queryID expired or unavailable)
        this.sendInsightsEvent('clickedObjectIDs', {
          eventName: 'Product Clicked',
          index: indexName,
          objectIDs: [objectID]
        });

        this.log('Click tracked (non-search fallback):', objectID);
      }
    });
  }

  /**
   * Get product objectID and position from lastHits
   * @param {string} productHandle - Product handle to find
   * @param {Object} adapter - Algolia adapter instance
   * @returns {Object} - { objectID, position }
   */
  getProductFromHits(productHandle, adapter) {
    // Try to get hits from adapter's lastHits property
    const hits = adapter?.lastHits || adapter?.search?.helper?.lastResults?.hits || [];

    const hitIndex = hits.findIndex(hit =>
      hit.handle === productHandle ||
      hit.objectID?.includes(productHandle)
    );

    if (hitIndex >= 0) {
      const hit = hits[hitIndex];
      return {
        objectID: hit.objectID || hit.id?.toString() || productHandle,
        position: hitIndex + 1 // 1-indexed position
      };
    }

    // Product not found in hits — cannot determine correct objectID
    // (index uses variant IDs, not handles, so falling back to handle would send invalid data)
    return { objectID: null, position: 1 };
  }

  /**
   * Get product position in search results
   */
  getProductPosition(productHandle) {
    const adapter = window.algoliaAdapter || window.algoliaCollectionPage;

    if (!adapter || !adapter.search) {
      return 1;
    }

    const results = adapter.search.helper.lastResults;

    if (!results) {
      return 1;
    }

    const index = results.hits.findIndex(hit => hit.handle === productHandle);
    return index >= 0 ? index + 1 : 1;
  }

  /**
   * Set up add to cart tracking
   */
  setupAddToCartTracking() {
    // Dedup guard: cart:item-added fires from AJAX /cart/add.js responses,
    // submit fires from form submissions. Both can trigger for the same action.
    // Track via cart:item-added (has richer detail) and skip submit if recent.
    let lastAtcTimestamp = 0;

    // Listen for Shopify add to cart events
    document.addEventListener('cart:item-added', (e) => {
      lastAtcTimestamp = Date.now();
      // Shopify /cart/add.js returns { items: [{ handle, product_id, ... }] }
      const productHandle = e.detail?.items?.[0]?.handle || e.detail?.productHandle || e.detail?.handle;

      if (!productHandle) {
        return;
      }

      // Get adapter and query ID (check search page adapter too)
      const adapter = window.algoliaCollectionPage || window.algoliaSearchPage || window.algoliaAdapter;
      const queryID = adapter?.lastQueryID || adapter?.search?.helper?.lastResults?.queryID;

      // Get proper objectID from hits
      const { objectID } = this.getProductFromHits(productHandle, adapter);
      const indexName = adapter?.search?.helper?.state?.index || this.config.indexName;

      if (!objectID) {
        this.log('Product not found in hits, skipping ATC tracking:', productHandle);
        return;
      }

      // Shopify /cart/add.js returns { items: [{ price, quantity, ... }] }
      // price is in cents (integer), e.g. 2999 = $29.99
      const cartItem = e.detail?.items?.[0];
      const price = cartItem ? (parseFloat(cartItem.price) / 100) || 0 : 0;
      const quantity = cartItem ? parseInt(cartItem.quantity, 10) || 1 : 1;
      const currency = window.Shopify?.currency?.active || 'USD';

      if (queryID) {
        // Search-attributed add to cart
        const atcEvent = {
          eventName: 'Product Added to Cart',
          eventSubtype: 'addToCart',
          index: indexName,
          objectIDs: [objectID],
          queryID: queryID,
          currency: currency
        };
        if (price > 0) {
          atcEvent.objectData = [{ price: price, quantity: quantity }];
          atcEvent.value = price * quantity;
        }
        this.sendInsightsEvent('addedToCartObjectIDsAfterSearch', atcEvent);

        // Store for conversion tracking via Shopify Web Pixel
        this.storeConversionData(queryID, objectID, indexName, {
          price: price, quantity: quantity, currency: currency
        });

        // Also store in cart attribution (backup for next add-to-cart)
        if (window.AlgoliaCartAttribution) {
          window.AlgoliaCartAttribution.store({
            queryID: queryID,
            objectID: objectID,
            handle: productHandle,
            indexName,
            price: price,
            quantity: quantity
          });
        }

        this.log('Add to cart tracked (search):', objectID);
      } else {
        // Non-search add to cart (direct PDP, homepage, etc.)
        const atcEvent = {
          eventName: 'Product Added to Cart',
          eventSubtype: 'addToCart',
          index: this.config.indexName,
          objectIDs: [objectID],
          currency: currency
        };
        if (price > 0) {
          atcEvent.objectData = [{ price: price, quantity: quantity }];
          atcEvent.value = price * quantity;
        }
        this.sendInsightsEvent('addedToCartObjectIDs', atcEvent);

        this.log('Add to cart tracked (non-search):', objectID);
      }
    });

    // Fallback for non-AJAX add-to-cart (full page form submissions).
    // submit fires BEFORE cart:item-added in AJAX flow, so we defer
    // to give cart:item-added (which has richer data) time to fire first.
    document.addEventListener('submit', (e) => {
      const form = e.target;

      if (form.getAttribute('action') === '/cart/add' || form.classList.contains('product-form')) {
        const productId = form.querySelector('input[name="id"]')?.value;

        if (productId) {
          // Read now: algolia-cart-attribution.js clears it on product-ajax:added,
          // which usually fires before the timeout below
          const attribution = window.AlgoliaCartAttribution?.get();
          setTimeout(() => {
            // If cart:item-added already handled this within 2s, skip
            if (Date.now() - lastAtcTimestamp < 2000) {
              this.log('ATC submit skipped — cart:item-added already handled');
              return;
            }
            this.trackAddToCartByVariantId(productId, attribution);
          }, 1000);
        }
      }
    });
  }

  /**
   * Track add to cart by variant ID
   */
  trackAddToCartByVariantId(variantId, attribution = window.AlgoliaCartAttribution?.get()) {
    if (!variantId) return;

    const objectID = String(variantId);
    const currency = window.Shopify?.currency?.active || 'USD';

    if (attribution && attribution.queryID) {
      // Always use the variant actually being added, not the stale attribution objectID
      const atcEvent = {
        eventName: 'Product Added to Cart',
        eventSubtype: 'addToCart',
        index: attribution.indexName || this.config.indexName,
        objectIDs: [objectID],
        queryID: attribution.queryID,
        currency: currency
      };
      if (attribution.price) {
        atcEvent.objectData = [{
          price: parseFloat(attribution.price),
          quantity: parseInt(attribution.quantity, 10) || 1
        }];
        atcEvent.value = parseFloat(attribution.price) * (parseInt(attribution.quantity, 10) || 1);
      }
      this.sendInsightsEvent('addedToCartObjectIDsAfterSearch', atcEvent);

      this.storeConversionData(attribution.queryID, objectID, attribution.indexName, {
        price: attribution.price ? parseFloat(attribution.price) : undefined,
        quantity: attribution.quantity ? parseInt(attribution.quantity, 10) : undefined,
        currency: currency
      });
      this.log('Add to cart by variant ID tracked (search):', objectID);
    } else {
      // Non-search add to cart
      this.sendInsightsEvent('addedToCartObjectIDs', {
        eventName: 'Product Added to Cart',
        eventSubtype: 'addToCart',
        index: this.config.indexName,
        objectIDs: [objectID],
        currency: currency
      });

      this.log('Add to cart by variant ID tracked (non-search):', objectID);
    }
  }

  /**
   * DEPRECATED: Conversion tracking is now handled exclusively by the Shopify
   * Web Pixel (algolia-conversion-pixel.js) which fires purchasedObjectIDsAfterSearch
   * with full objectData (price, quantity, discount). This fallback was causing
   * duplicate purchase events in Algolia's event pipeline.
   * Kept as no-op for backward compatibility.
   */
  setupConversionTracking() {
    this.log('setupConversionTracking() is deprecated — Web Pixel handles all purchase tracking');
  }

  /**
   * Extract variant ID from checkout item
   * Our Algolia index uses variant IDs as objectIDs
   */
  extractVariantIdFromItem(item) {
    if (item.variant_id) {
      return String(item.variant_id);
    }

    return null;
  }

  /**
   * Get analytics metrics
   */
  getMetrics() {
    return {
      searchesTracked: this.searchesTracked || 0,
      clicksTracked: this.clicksTracked || 0,
      conversionsTracked: this.conversionsTracked || 0,
      zeroResultsQueries: this.zeroResultsQueries || []
    };
  }

  /**
   * Convert Algolia helper state filters to a compact string
   * Uses shared utility from AlgoliaShared
   */
  getFiltersString(state) {
    return window.AlgoliaShared?.getFiltersString(state) || '';
  }

  /**
   * Safe wrapper for Algolia Insights aa() calls.
   * Prevents ReferenceError if Search Insights library is unavailable.
   */
  sendInsightsEvent(method, params) {
    if (typeof aa === 'function') {
      try {
        aa(method, params);
      } catch (e) {
        this.log('Insights event failed:', method, e.message);
      }
    } else {
      this.log('Insights not available, skipping:', method);
    }
  }

  /**
   * Debug logging
   */
  log(...args) {
    if (this.config && this.config.debug) {
      // eslint-disable-next-line no-console
      console.log('[Algolia Analytics]', ...args);
    }
  }
}

/**
 * Algolia Health Check — diagnostic utility for verifying analytics pipeline
 * Only exposed when debug mode is enabled (window.algoliaConfig.debug)
 * Usage: AlgoliaHealthCheck.run() from browser console
 */
if (window.algoliaConfig?.debug) {
  window.AlgoliaHealthCheck = {
    version: 'Phase 1+2+3 (v1.29.8+)',

    run: function () {
      const results = [];

      // 1. Insights library loaded
      const aaLoaded = typeof aa === 'function';
      results.push({
        Check: 'Insights library',
        Status: aaLoaded ? 'PASS' : 'FAIL',
        Detail: aaLoaded ? 'aa() available' : 'aa() not found'
      });

      // 2. Analytics initialized
      const analyticsReady = window.lampsAlgoliaAnalytics?.insightsReady === true ||
        window.algoliaAnalytics?.insightsReady === true;
      results.push({
        Check: 'Analytics initialized',
        Status: analyticsReady ? 'PASS' : 'FAIL',
        Detail: analyticsReady ? 'Ready' : 'Not initialized'
      });

      // 3. User token set
      let userToken = null;
      try {
        if (aaLoaded) aa('getUserToken', null, function (err, token) { userToken = token; });
      } catch (_e) { /* getUserToken may not be available */ }
      if (!userToken) {
        const cookieMatch = document.cookie.match(/_ALGOLIA=([^;]+)/);
        if (cookieMatch) userToken = decodeURIComponent(cookieMatch[1]);
      }
      if (!userToken) userToken = localStorage.getItem('algolia_user_token');
      results.push({
        Check: 'User token',
        Status: userToken ? 'PASS' : 'FAIL',
        Detail: userToken ? userToken.substring(0, 16) + (userToken.length > 16 ? '...' : '') : 'No token found'
      });

      // 4. Config valid
      const cfg = window.algoliaConfig;
      const configValid = cfg && cfg.appId && cfg.apiKey && cfg.indexName;
      results.push({
        Check: 'Config valid',
        Status: configValid ? 'PASS' : 'FAIL',
        Detail: configValid ? cfg.appId : 'Missing: ' + [
          !cfg?.appId && 'appId',
          !cfg?.apiKey && 'apiKey',
          !cfg?.indexName && 'indexName'
        ].filter(Boolean).join(', ')
      });

      // 5. Cart attribution module
      const cart = window.AlgoliaCartAttribution;
      const cartMethods = cart && typeof cart.store === 'function' &&
        typeof cart.get === 'function' && typeof cart.clear === 'function';
      results.push({
        Check: 'Cart attribution module',
        Status: cartMethods ? 'PASS' : 'FAIL',
        Detail: cartMethods ? '3 methods available' : (cart ? 'Missing methods' : 'Not loaded')
      });

      // 6. Predictive search
      const predictive = window.AlgoliaPredictiveSearch ||
        typeof window.AlgoliaPredictiveSearchClass === 'function';
      results.push({
        Check: 'Predictive search',
        Status: predictive ? 'PASS' : 'SKIP',
        Detail: predictive ? 'Class loaded' : 'Not loaded'
      });

      // 7. InstantSearch insights
      const isCollectionPage = !!window.algoliaCollectionPage;
      const isSearchPage = !!window.algoliaSearchPage;
      const insightsMiddleware = isCollectionPage || isSearchPage;
      results.push({
        Check: 'InstantSearch insights',
        Status: insightsMiddleware ? 'PASS' : 'SKIP',
        Detail: insightsMiddleware
          ? 'Active on ' + (isCollectionPage ? 'collection' : 'search') + ' page'
          : 'Not on collection/search page'
      });

      // 8. Session attribution
      let sessionData = null;
      try { sessionData = sessionStorage.getItem('_algolia_attribution'); } catch (_e) { /* noop */ }
      if (sessionData) {
        let parsed = null;
        try { parsed = JSON.parse(sessionData); } catch (_e) { /* noop */ }
        const age = parsed?.timestamp ? Math.round((Date.now() - parsed.timestamp) / 60000) : null;
        results.push({
          Check: 'Session attribution',
          Status: 'PASS',
          Detail: age !== null ? 'Stored ' + age + 'm ago' : 'Data present'
        });
      } else {
        results.push({
          Check: 'Session attribution',
          Status: 'SKIP',
          Detail: 'No attribution in session'
        });
      }

      // Output
      console.group('%c Algolia Health Check %c ' + this.version, 'background:#5468ff;color:#fff;padding:2px 6px;border-radius:3px', 'color:#888');
      console.table(results);
      console.groupEnd();

      return results;
    }
  };
}

// Initialize analytics when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (window.algoliaConfig && window.algoliaConfig.features.analytics) {
    window.algoliaAnalytics = new LampsAlgoliaAnalytics();
  }
});

// Make class available globally (using unique name to avoid conflict with official Algolia Search Insights)
window.LampsAlgoliaAnalytics = LampsAlgoliaAnalytics;
